class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.timeLeft = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    this.timer = null;
    this.elements = {};
    this.initialDifference = null;
    this.initialStartTime = null; 
    
    // Default translations
    this.translations = {
      days: {
        singular: 'Tag',
        plural: 'Tage'
      },
      hours: {
        singular: 'Stunde',
        plural: 'Stunden'
      },
      minutes: {
        singular: 'Minute',
        plural: 'Minuten'
      },
      seconds: {
        singular: 'Sekunde',
        plural: 'Sekunden'
      }
    };
  }

  static get observedAttributes() {
    return ['target-date', 'translations'];
  }

  connectedCallback() {
    this.link = this.getAttribute('link');
    this.size = this.getAttribute('timer-size');
    const $this = this
    if (this.link) {
      $this.style.cursor = 'pointer'
    }
    this.addEventListener('click', () => {
      if ($this.link) {
        window.location.href = $this.link;
      }
    });
    this.loadTranslations();
    this.createElements();
    this.startTimer();
  }

  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'target-date' && oldValue !== newValue) {
      this.startTimer();
    }
    if (name === 'translations' && oldValue !== newValue) {
      this.loadTranslations();
      this.updateDisplay(); // Update labels with new translations
    }
  }

  loadTranslations() {
    const translationsAttr = this.getAttribute('translations');
    if (translationsAttr) {
      try {
        const customTranslations = JSON.parse(translationsAttr);
        this.translations = {
          ...this.translations,
          ...customTranslations
        };
      } catch (e) {
        console.error('Invalid translations format:', e);
      }
    }
  }

  createElements() {
    // Container
    const container = document.createElement('div');
    container.className = 'ds-timer__container';

    const countdownContainer = document.createElement('div');
    countdownContainer.className = 'ds-timer__countdown';

    // Create number row
    const numberRow = document.createElement('div');
    numberRow.className = 'ds-timer__number-row';

    // Create label row
    //const labelRow = document.createElement('div');
    //labelRow.className = 'ds-timer__label-row';

    // Create units (days, hours, minutes, seconds)
    const units = ['days', 'hours', 'minutes', 'seconds'];

    units.forEach((unit, index) => {
      // Create number container
      const numberContainer = document.createElement('div');
      numberContainer.className = 'ds-timer__number-container rounded-sm';

      // Create number element
      const number = document.createElement('span');
      if (this.size === 'large') {
        number.className = 'ds-timer__number h0 text-font bold';
      } else {
        number.className = 'ds-timer__number h3 text-font bold';
      }
      this.elements[`${unit}-number`] = number;
      numberContainer.appendChild(number);

      // Create label container
      //const labelContainer = document.createElement('div');
      //labelContainer.className = 'ds-timer__label-container';

      // Create label element
      const label = document.createElement('span');
      label.className = 'ds-timer__label bold';
      this.elements[`${unit}-label`] = label;
      numberContainer.appendChild(label);

      // Add containers to their respective rows
      numberRow.appendChild(numberContainer);
      //labelRow.appendChild(labelContainer);

      // Add separator if not last item
      /*if (index < units.length - 1) {
        const numberSeparator = document.createElement('div');
        numberSeparator.className = 'ds-timer__separator';
        numberSeparator.textContent = ':';
        numberRow.appendChild(numberSeparator);
      }*/
    });

    // Add both rows to countdown container
    countdownContainer.appendChild(numberRow);
    //countdownContainer.appendChild(labelRow);

    // Add countdown container to main container
    container.appendChild(countdownContainer);

    // Clear and append new content
    this.replaceChildren(container);
  }

  calculateTimeLeft() {
    const useTargetDate = this.getAttribute('use-target-date') === 'true';
    const useTargetTime = this.getAttribute('use-target-time') === 'true';
    const now = new Date();
    const targetDate = new Date(this.getAttribute('target-date'));
    
    let difference;
    
    if (useTargetTime) {
      if (this.initialDifference === null) {
        const targetTime = new Date();
        const targetTimeValue = parseInt(this.getAttribute('target-time')) || 0;
        targetTime.setTime(targetTime.getTime() + targetTimeValue);
        this.initialDifference = targetTime.getTime() - now.getTime();
        this.initialStartTime = now.getTime();
      }
      // Calculate the current difference by subtracting the elapsed time
      const elapsedTime = new Date().getTime() - this.initialStartTime;
      difference = this.initialDifference - elapsedTime;
    } else if (useTargetDate) {
      difference = targetDate.getTime() - now.getTime();
    }

    if (difference <= 0) {
      this.dispatchEvent(new CustomEvent('countdown-complete'));
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }

  startTimer() {
    if (this.timer) {
      clearInterval(this.timer);
    }

    this.timeLeft = this.calculateTimeLeft();
    this.updateDisplay();

    this.timer = setInterval(() => {
      this.timeLeft = this.calculateTimeLeft();
      this.updateDisplay();

      if (Object.values(this.timeLeft).every(value => value === 0)) {
        clearInterval(this.timer);
      }
    }, 1000);
  }

  padNumber(num) {
    return String(num).padStart(2, '0');
  }

  getLabel(unit, value) {
    const translation = this.translations[unit];
    if (!translation) return '';
    return value === 1 ? translation.singular : translation.plural;
  }

  updateDisplay() {
    Object.entries(this.timeLeft).forEach(([unit, value]) => {
      // Update number
      const numberElement = this.elements[`${unit}-number`];
      if (numberElement) {
        numberElement.textContent = this.padNumber(value);
      }

      // Update label with singular/plural form
      const labelElement = this.elements[`${unit}-label`];
      if (labelElement) {
        labelElement.textContent = this.getLabel(unit, value);
      }
    });
  }
}

// Register the web component
customElements.define('countdown-timer', CountdownTimer);